import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { PRIVACY_LAST_UPDATED, PRIVACY_EMAIL } from "../lib/constants";

/**
 * Public privacy policy page rendered at `/privacy`. Apple App Store + GDPR
 * boilerplate tailored to DAIONE's actual data flows:
 *   - Personal profile (email, age, sex, height, weight, goals) stored in MongoDB
 *   - Workout / food / weight logs stored in MongoDB
 *   - Camera + Photos access used for food-photo calorie estimation and
 *     private progress photos (server-side encrypted-at-rest blobs, JWT-gated reads)
 *   - 3rd-party processors: Anthropic Claude (food + workout AI), Stripe
 *     (subscription billing), Spotify (OAuth, optional music playback)
 *
 * This page intentionally avoids legal jargon while still being App-Store-ready.
 * Have your legal counsel review before App Store submission if your jurisdiction
 * requires explicit attorney-drafted notices (CCPA + UK DPA layered terms etc.).
 */
const Section = ({ title, children, testid }) => (
  <section className="mb-10" data-testid={testid}>
    <h2 className="font-display text-2xl sm:text-3xl text-white mb-3">{title}</h2>
    <div className="text-white/70 leading-relaxed space-y-3 text-sm sm:text-base">{children}</div>
  </section>
);

export default function PrivacyPage() {
  const lastUpdated = PRIVACY_LAST_UPDATED;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white/50 hover:text-white mb-8"
          data-testid="privacy-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="w-5 h-5 text-[#6B9BFF]" />
          <div className="label-eyebrow text-[#6B9BFF]">Privacy</div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none mb-4">
          Your data. Your rules.
        </h1>
        <p className="text-white/60 text-sm mb-12">
          Last updated: <span className="text-white/80">{lastUpdated}</span>
        </p>

        <Section title="Who we are" testid="privacy-section-who">
          <p>
            DAIONE ("we", "us", "our") is a fitness coaching app that builds personalized workout
            programs and tracks nutrition and body progress. We are the data controller for the
            information you provide directly through the app and website.
          </p>
          <p>
            For privacy questions, requests for data access, or requests for deletion, contact us at{" "}
            <a href={`mailto:${PRIVACY_EMAIL}`} className="text-[#6B9BFF] underline">{PRIVACY_EMAIL}</a>.
          </p>
        </Section>

        <Section title="What we collect" testid="privacy-section-collect">
          <p>We only collect what's needed to make the coaching work:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong className="text-white">Account info</strong> — email and a hashed password
              (bcrypt, never stored in plaintext).
            </li>
            <li>
              <strong className="text-white">Profile</strong> — name, age, sex, height, weight,
              fitness level, training goals, equipment, days/week, daily calorie target, and
              optional macro split.
            </li>
            <li>
              <strong className="text-white">Activity</strong> — workouts you generate, sets you
              log, weight history, body measurements, daily checklist completions, streaks, and
              personal records.
            </li>
            <li>
              <strong className="text-white">Nutrition</strong> — food entries including any
              photos you take to estimate calories.
            </li>
            <li>
              <strong className="text-white">Progress photos</strong> — body progress images you
              choose to upload. Private by default and only readable by your authenticated
              account.
            </li>
            <li>
              <strong className="text-white">Technical logs</strong> — basic server logs (IP,
              user-agent, timestamps) kept for security and debugging for up to 30 days.
            </li>
          </ul>
          <p>
            We do <strong className="text-white">not</strong> collect contacts, precise location,
            device identifiers for advertising, or HealthKit data unless you explicitly enable a
            future integration that requests it.
          </p>
        </Section>

        <Section title="Camera & photos" testid="privacy-section-camera">
          <p>
            On iOS the app may request <strong className="text-white">Camera</strong> and{" "}
            <strong className="text-white">Photo Library</strong> permissions for two reasons:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Snap or pick a photo of a meal so our AI can estimate calories and macros (the
              photo is sent to Anthropic for vision analysis and is not stored permanently after
              the estimate is returned, unless you save the meal — in which case a thumbnail is
              kept against your meal log).
            </li>
            <li>
              Capture progress photos. These are encrypted in transit (HTTPS) and stored on our
              servers tied to your account. They are <strong className="text-white">never
              public</strong> and are only retrievable with your valid auth token.
            </li>
          </ul>
          <p>
            You can revoke camera and photo permissions at any time in iOS Settings → DAIONE.
          </p>
        </Section>

        <Section title="How we use your data" testid="privacy-section-use">
          <ul className="list-disc pl-6 space-y-2">
            <li>Build personalized workout programs and progression schedules.</li>
            <li>Compute TDEE, daily calorie targets, and macro splits from your profile.</li>
            <li>Render progress charts, streak counters, and shareable PR/goal cards.</li>
            <li>Process subscription payments (via Stripe — see Third Parties below).</li>
            <li>Send transactional emails (account recovery, billing receipts). We do not send marketing without your opt-in.</li>
            <li>Diagnose bugs and prevent abuse.</li>
          </ul>
          <p>
            We do <strong className="text-white">not</strong> sell your personal data and we do
            not use your workout, food, or photo data to train third-party models without your
            explicit opt-in.
          </p>
        </Section>

        <Section title="Third-party processors" testid="privacy-section-thirdparties">
          <p>We share the minimum data necessary with these vetted processors:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong className="text-white">Anthropic (Claude)</strong> — workout and food
              text/image prompts. Subject to Anthropic's privacy policy. No account identifiers
              are sent; only the prompt content for the request.
            </li>
            <li>
              <strong className="text-white">Stripe</strong> — your name, email, and payment
              method (handled directly by Stripe; we never see your card number). Subject to
              Stripe's privacy policy.
            </li>
            <li>
              <strong className="text-white">Spotify</strong> — optional. If you link Spotify we
              receive a scoped OAuth token to read your playlists / now-playing for in-app music.
              Subject to Spotify's privacy policy. Disconnect anytime from Profile → Spotify.
            </li>
            <li>
              <strong className="text-white">Cloud infrastructure</strong> — MongoDB Atlas
              (database) and a managed Kubernetes provider (compute). Data is processed in
              regions with GDPR-adequate protections.
            </li>
          </ul>
        </Section>

        <Section title="Your rights (GDPR / UK DPA / CCPA)" testid="privacy-section-rights">
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong className="text-white">Access</strong> a copy of the personal data we hold about you.</li>
            <li><strong className="text-white">Rectify</strong> inaccurate or incomplete data — most fields are editable directly in the app.</li>
            <li><strong className="text-white">Erase</strong> your account and all associated data. Use the delete-account option in Profile or email us at <a href={`mailto:${PRIVACY_EMAIL}`} className="text-[#6B9BFF] underline">{PRIVACY_EMAIL}</a>.</li>
            <li><strong className="text-white">Restrict</strong> or <strong className="text-white">object</strong> to certain processing.</li>
            <li><strong className="text-white">Port</strong> your data to another service in a machine-readable format (we provide JSON exports on request).</li>
            <li><strong className="text-white">Withdraw consent</strong> for optional integrations (Spotify, photo uploads) at any time.</li>
            <li><strong className="text-white">Complain</strong> to your local data protection authority if you feel we have mishandled your data.</li>
          </ul>
          <p>
            We respond to verified requests within 30 days.
          </p>
        </Section>

        <Section title="Data retention" testid="privacy-section-retention">
          <ul className="list-disc pl-6 space-y-2">
            <li>Account + activity data — kept while your account is active.</li>
            <li>Server logs — up to 30 days.</li>
            <li>Cancelled subscriptions — billing records kept 7 years to satisfy tax law.</li>
            <li>
              On account deletion all personal profile, workout, food, photo, and progress data is
              removed within 30 days. Anonymised aggregates may be retained for product analytics.
            </li>
          </ul>
        </Section>

        <Section title="Security" testid="privacy-section-security">
          <p>
            All traffic is encrypted with TLS. Passwords are hashed with bcrypt. Database access
            is restricted to the application service account and is logged. Progress photos are
            served only after JWT validation tied to your user id. We perform security reviews
            on integration code changes and rotate secrets on a regular cadence.
          </p>
        </Section>

        <Section title="Children" testid="privacy-section-children">
          <p>
            DAIONE is intended for users aged 16 and older. We do not knowingly collect personal
            data from anyone under 16. If you believe a child has used the app, contact us and
            we will delete the account.
          </p>
        </Section>

        <Section title="Changes to this policy" testid="privacy-section-changes">
          <p>
            We will update the "Last updated" date above whenever this policy changes. Material
            changes are announced via in-app notice and / or email.
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-white/10 text-xs text-white/40 flex flex-col sm:flex-row gap-2 sm:justify-between">
          <span>© 2026 DAIONE · Train. Track. Transform.</span>
          <Link to="/" className="hover:text-white">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
